import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Users, CheckCircle2, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useProject } from '@/hooks/useProject';

interface ReferralSelectorProps {
  mainSubmissionId: number;
  selectedReferralIds: number[];
  onSelectionChange: (ids: number[]) => void;
  children?: React.ReactNode;
  inviterEmail?: string;
}

interface ReferralSubmission {
  id: number;
  jotform_submission_id: string;
  email: string | null;
  code: string | null;
  jotform_created_at: string;
  raw_answers: Record<string, any>;
}

export function ReferralSelector({
  mainSubmissionId,
  selectedReferralIds,
  onSelectionChange,
  children,
  inviterEmail
}: ReferralSelectorProps) {
  const { projectClient } = useProject();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Obtener referidos de la submission principal (o todos del email si se proporciona)
  const { data: referrals = [], isLoading } = useQuery({
    queryKey: ['submission-referrals', mainSubmissionId, inviterEmail],
    queryFn: async () => {
      if (!mainSubmissionId) return [];

      // Si tenemos email, buscar referidos de todos los submissions del email
      if (inviterEmail) {
        // Paso 1: Obtener todos los submission IDs del invitador
        const { data: inviterSubmissions, error: inviterError } = await supabase
          .from('jotform_submissions')
          .select('id')
          .eq('email', inviterEmail)
          .eq('is_deleted_in_jotform', false);

        if (inviterError || !inviterSubmissions?.length) {
          return [];
        }

        const inviterIds = inviterSubmissions.map(s => s.id);

        // Paso 2: Obtener referidos de todos esos submissions
        const { data, error } = await supabase
          .from('jotform_submissions')
          .select(`
            id,
            jotform_submission_id,
            email,
            code,
            jotform_created_at,
            raw_answers
          `)
          .in('parent_submission_id', inviterIds)
          .eq('is_deleted_in_jotform', false)
          .order('jotform_created_at', { ascending: false });

        if (error) {
          console.error('Error fetching referrals:', error);
          return [];
        }

        return data as ReferralSubmission[];
      }

      // Fallback: buscar solo por submission ID
      const { data, error } = await supabase
        .from('jotform_submissions')
        .select(`
          id,
          jotform_submission_id,
          email,
          code,
          jotform_created_at,
          raw_answers
        `)
        .eq('parent_submission_id', mainSubmissionId)
        .eq('is_deleted_in_jotform', false)
        .order('jotform_created_at', { ascending: false });

      if (error) {
        console.error('Error fetching referrals:', error);
        return [];
      }

      return data as ReferralSubmission[];
    },
    enabled: !!mainSubmissionId && isOpen
  });

  // Filtrar referidos por término de búsqueda
  const filteredReferrals = referrals.filter(referral => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const email = referral.email?.toLowerCase() || '';
    const code = referral.code?.toLowerCase() || '';
    const name = getSubmissionName(referral).toLowerCase();
    
    return email.includes(searchLower) || 
           code.includes(searchLower) || 
           name.includes(searchLower);
  });

  // Obtener nombre de la submission desde raw_answers
  const getSubmissionName = (submission: ReferralSubmission): string => {
    if (!submission.raw_answers) return 'N/A';

    let firstName = '';
    let lastName = '';

    Object.values(submission.raw_answers).forEach((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      if (name === 'nombre' || name === 'referido-nombre') {
        firstName = answer.answer || answer.text || '';
      }
      if (name === 'apellido' || name === 'referido-apellido2') {
        lastName = answer.answer || answer.text || '';
      }
    });

    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return 'N/A';
  };

  // Obtener email de la submission
  const getSubmissionEmail = (submission: ReferralSubmission): string => {
    if (!submission.raw_answers) return submission.email || 'N/A';

    const emailAnswer = Object.values(submission.raw_answers).find((answer: any) => {
      const name = answer?.name?.toLowerCase() || '';
      const text = answer?.text?.toLowerCase() || '';
      return name === 'email' || name === 'correo' || name === 'referido-email' ||
             name.includes('correoelectronico') || name.includes('correo-electronico') ||
             text.includes('correo electrónico');
    });

    return (emailAnswer as any)?.answer || submission.email || 'N/A';
  };

  // Manejar selección individual
  const handleReferralToggle = (referralId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedReferralIds, referralId]);
    } else {
      onSelectionChange(selectedReferralIds.filter(id => id !== referralId));
    }
  };

  // Seleccionar todos
  const handleSelectAll = () => {
    onSelectionChange(filteredReferrals.map(r => r.id));
  };

  // Deseleccionar todos
  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  // Seleccionar todos al abrir si no hay ninguno seleccionado
  useEffect(() => {
    if (isOpen && referrals.length > 0 && selectedReferralIds.length === 0) {
      onSelectionChange(referrals.map(r => r.id));
    }
  }, [isOpen, referrals, selectedReferralIds.length, onSelectionChange]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Gestionar Referidos ({selectedReferralIds.length})
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Seleccionar Referidos
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Controles */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, email o código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleSelectAll}
                variant="outline"
                size="sm"
                disabled={filteredReferrals.length === 0}
              >
                Seleccionar Todos
              </Button>
              <Button
                onClick={handleDeselectAll}
                variant="outline"
                size="sm"
                disabled={selectedReferralIds.length === 0}
              >
                Deseleccionar Todos
              </Button>
            </div>
          </div>

          {/* Resumen */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>
              {selectedReferralIds.length} de {referrals.length} referidos seleccionados
            </span>
            {filteredReferrals.length !== referrals.length && (
              <span>
                ({filteredReferrals.length} mostrados)
              </span>
            )}
          </div>

          {/* Lista de referidos */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-pulse">Cargando referidos...</div>
              </div>
            ) : filteredReferrals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'No se encontraron referidos con ese término' : 'No hay referidos para esta submission'}
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {filteredReferrals.map((referral) => {
                  const isSelected = selectedReferralIds.includes(referral.id);
                  const name = getSubmissionName(referral);
                  const email = getSubmissionEmail(referral);
                  
                  return (
                    <div
                      key={referral.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-200' 
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => 
                          handleReferralToggle(referral.id, checked as boolean)
                        }
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{name}</span>
                          {isSelected && (
                            <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="truncate">{email}</span>
                          {referral.code && (
                            <>
                              <span>•</span>
                              <Badge variant="outline" className="text-xs">
                                {referral.code}
                              </Badge>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(referral.jotform_created_at).toLocaleDateString('es-ES')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              onClick={() => setIsOpen(false)}
              variant="outline"
            >
              Cerrar
            </Button>
            <Button
              onClick={() => setIsOpen(false)}
              disabled={selectedReferralIds.length === 0}
            >
              Aplicar Selección ({selectedReferralIds.length})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

